/**
 * Content Moderation Service
 * Prüft Benutzereingaben auf verbotene Wörter (Schimpfwörter, Rassismus, Sexismus, Drogen, Gewalt).
 */

// Verbotene Wörter nach Kategorie (normalisierte Form, ohne Umlaute)
const BLOCKED_WORDS: string[] = [
  // Schimpfwörter
  'scheisse', 'scheiße', 'arschloch', 'wichser', 'hurensohn', 'fotze', 'fick',
  'ficken', 'gefickt', 'ficker', 'missgeburt', 'bastard', 'drecksau', 'vollidiot',
  'depp', 'trottel', 'idiot', 'spacko', 'spasti', 'behindert', 'schwuchtel',
  'tunte', 'schlampe', 'nutte', 'hure', 'pisser', 'penner', 'assi',
  'kackbratze', 'dumpfbacke', 'arsch', 'kacke', 'verdammt',
  
  // Rassismus / Diskriminierung
  'neger', 'nigger', 'kanake', 'kanaке', 'zigeuner', 'kameltreiber',
  'schlitzauge', 'kümmeltürke', 'kuemmeltuerke', 'bimbo', 'untermensch',
  'rassenschande', 'sieg heil', 'heil hitler', 'nazi',
  
  // Sexismus
  'fickstück', 'fickstueck', 'sexobjekt', 'vergewaltigen', 'vergewaltigung',
  
  // Drogen
  'kokain', 'heroin', 'crystal meth', 'crack',
  
  // Gewalt
  'umbringen', 'abstechen', 'abknallen', 'totschlagen', 'toeten',
  'ermorden', 'massakrieren', 'abschlachten',
];

/**
 * Normalisiert einen Text für den Vergleich:
 * - Kleinbuchstaben
 * - Umlaute auflösen
 * - Leetspeak ersetzen
 * - Sonderzeichen entfernen (außer Leerzeichen)
 */
function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/4/g, 'a').replace(/3/g, 'e').replace(/0/g, 'o').replace(/1/g, 'i').replace(/\$/g, 's').replace(/@/g, 'a')
    .replace(/[^a-z\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Vorberechnete normalisierte Wortliste
const NORMALIZED_BLOCKED = BLOCKED_WORDS.map(w => normalize(w));

/**
 * Runtime-Override (gesetzt von useContentModerationConfig auf Basis der DB-Tabelle
 * `content_moderation_settings`). Wenn null → Default-Liste aus dieser Datei.
 */
let runtimeBlockedWords: string[] | null = null;
let runtimeNormalizedBlocked: string[] | null = null;
let runtimeEnabled: boolean = true;

export function setModerationConfig(config: { enabled: boolean; blockedWords: string[] | null }) {
  runtimeEnabled = config.enabled;
  if (config.blockedWords && config.blockedWords.length > 0) {
    runtimeBlockedWords = config.blockedWords;
    runtimeNormalizedBlocked = config.blockedWords.map(w => normalize(w));
  } else {
    runtimeBlockedWords = null;
    runtimeNormalizedBlocked = null;
  }
}

/**
 * Prüft, ob `needle` (normalisiert) als ganzes Wort (oder zusammenhängende Tokenfolge)
 * in `haystack` (normalisiert) vorkommt. Verhindert Substring-Treffer wie "tunte"
 * in "testunternehmen".
 */
function containsWholeWord(haystack: string, needle: string): boolean {
  if (!needle) return false;
  // \b funktioniert nach Normalisierung (nur a-z + Leerzeichen) zuverlässig.
  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`\\b${escaped}\\b`, 'i');
  return re.test(haystack);
}

export interface ModerationResult {
  status: 'clean' | 'blocked';
  matches: string[];
}

/**
 * Prüft eine Eingabe auf verbotene Inhalte.
 */
export function moderateInput(text: string): ModerationResult {
  if (!text || !text.trim()) {
    return { status: 'clean', matches: [] };
  }

  if (!runtimeEnabled) {
    return { status: 'clean', matches: [] };
  }

  const normalizedInput = normalize(text);
  const matches: string[] = [];

  const sourceList = runtimeBlockedWords ?? BLOCKED_WORDS;
  const normalizedList = runtimeNormalizedBlocked ?? NORMALIZED_BLOCKED;

  for (let i = 0; i < normalizedList.length; i++) {
    const blockedWord = normalizedList[i];
    if (containsWholeWord(normalizedInput, blockedWord)) {
      matches.push(sourceList[i]);
    }
  }

  return {
    status: matches.length > 0 ? 'blocked' : 'clean',
    matches: [...new Set(matches)],
  };
}
